import type { FC, ReactNode } from 'react'
import type { Review } from '@/types'

import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ReviewCardProps {
    review: Review
    className?: string
    tone?: 'dark' | 'light'
}

export const Stars: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <span className={cn('inline-flex items-center gap-0.5 text-amber-soft', className)} aria-label="5 out of 5 stars" role='img'>
            {[0, 1, 2, 3, 4].map(index => (
                <Star key={index} className='size-4 fill-current' aria-hidden='true' />
            ))}
        </span>
    )
}

export const ReviewCard: FC<ReviewCardProps> = ({ review, className, tone = 'dark' }): ReactNode => {
    const onDark = tone === 'light'

    return (
        <figure
            className={cn(
                'flex h-full flex-col justify-between gap-10 rounded-[26px] border p-7 sm:p-8',
                onDark ? 'border-cream/10 bg-cream/[0.04] text-cream' : 'border-ink/[0.08] bg-card text-ink',
                className
            )}
        >
            <div>
                <div className='flex items-center justify-between gap-4'>
                    <Stars />

                    {review.type && (
                        <span className={cn('font-mono text-[10.5px] uppercase tracking-[0.16em]', onDark ? 'text-cream/45' : 'text-slate')}>
                            {review.type}
                        </span>
                    )}
                </div>

                <blockquote className='mt-6 font-heading text-[1.35rem] font-medium leading-[1.3] tracking-[-0.02em] text-balance'>
                    <p>“{review.quote}”</p>
                </blockquote>
            </div>

            <figcaption className='flex items-center gap-3'>
                <span
                    aria-hidden='true'
                    className={cn(
                        'flex size-10 items-center justify-center rounded-full font-heading text-sm font-semibold',
                        onDark ? 'bg-cream/10 text-cream' : 'bg-sand text-ink'
                    )}
                >
                    {review.name.charAt(0)}
                </span>

                <span className='text-[14.5px]'>
                    <span className='block font-medium'>{review.name}</span>
                    <span className={cn('block', onDark ? 'text-cream/55' : 'text-slate')}>{review.town}</span>
                </span>
            </figcaption>
        </figure>
    )
}
